import type { GridColDef, GridRenderCellParams, GridRowId } from '@mui/x-data-grid-pro';
import type { IntelligenceRequest } from '@shared/domain/intelligenceRequest/IntelligenceRequest';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import moment from 'moment';
import { useContext, useEffect, useRef, useState } from 'react';
import CompanyLogo from '@shared/components/CompanyLogo';
import { ModernDataTable } from '@shared/components/DataTable';
import DynamicIcon, { IconName } from '@shared/components/Icon';
import { UserContext } from '@/shared/contexts/UserContext';
import { IntelligenceRequestStatus } from '@shared/domain/intelligenceRequest/IntelligenceRequestStatus';
import { useApi } from '@shared/services/api';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import { useSnackbar } from '@shared/services/SnackbarService';
import CreateIntelligenceRequestModal from '@/scenes/review-reader/components/CreateIntelligenceRequestModal';
import { renderStatusChip } from '../helpers/AdminIntelligenceRequestHelpers';

const AdminIntelligenceRequests: React.FC = () => {
  const api = useApi();
  const { user } = useContext(UserContext);
  const { showSnackbar } = useSnackbar();
  const { openMessage } = useMessageService();
  const apiRef = useGridApiRef();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; row: any } | null>(null);
  const [expandedRowIds, setExpandedRowIds] = useState<GridRowId[]>([]);
  const [expandedAuthorRowIds, setExpandedAuthorRowIds] = useState<GridRowId[]>([]);

  const fetchAllIntelligenceRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get('/intelligence-request/admin');
      setItems(res.data);
    } catch {
      showSnackbar('Failed to load intelligence requests.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsActive = async (request: IntelligenceRequest) => {
    try {
      const result = await api.post(`/intelligence-request/${request.id}/activate`);

      if (result.success) {
        await api.post('/intelligence-request/share', {
          requestDescription: request.description,
          companyName: request.companyName || '',
          requestId: request.id,
          companyId: request.companyId,
          amountUsd: request.amountUsd,
          isPrivate: request.isPrivate,
          reviewId: request.reviewId,
        });
      }

      fetchAllIntelligenceRequests();

      if (result.charged) {
        showSnackbar('Request fulfilled — issuer successfully charged.', 'success');
      } else {
        showSnackbar('Request marked as active.', 'success');
      }
    } catch {
      showSnackbar('Failed to mark request as active.', 'error');
    }
  };

  const handleFulfillIntelligenceRequest = (row: any) => {
    const amountUsd = row.amountUsd ?? 0;
    const feeUsd = parseFloat((amountUsd * 0.2).toFixed(2));
    const totalUsd = parseFloat((amountUsd * 1.2).toFixed(2));

    const chargeBody =
      amountUsd > 0 ? (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will mark the request as fulfilled and charge the issuer's card on file.
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: 'rgba(59, 111, 217, 0.06)',
              border: '1px solid rgba(59, 111, 217, 0.18)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Contributor payment
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                ${amountUsd}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Kandir facilitation fee (20%)
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                ${feeUsd}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight={700}>
                Total charged to issuer
              </Typography>
              <Typography variant="body2" fontWeight={700} color="success.main">
                ${totalUsd}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2">
          No bounty amount set — the request will be marked as fulfilled but no charge will be taken.
        </Typography>
      );

    openMessage({
      modalType: 'action',
      header: 'Mark as Fulfilled',
      customBody: chargeBody,
      confirmLabel: amountUsd > 0 ? 'Charge & Fulfill' : 'Mark as Fulfilled',
      confirmButtonColor: 'success',
      onConfirm: async () => {
        try {
          const result = await api.patch(`/intelligence-request/${row.id}`, {
            ...row,
            status: IntelligenceRequestStatus.FULFILLED,
          });
          fetchAllIntelligenceRequests();
          if (result.charged) {
            showSnackbar('Request fulfilled — issuer successfully charged.', 'success');
          } else {
            showSnackbar('Request marked as fulfilled.', 'success');
          }
        } catch {
          showSnackbar('Failed to fulfill request.', 'error');
        }
      },
    });
  };

  const handleDeclineIntelligenceRequest = async (request: IntelligenceRequest) => {
    try {
      const payload = {
        ...request,
        status: IntelligenceRequestStatus.REJECTED,
      };
      await api.patch(`/intelligence-request/${request.id}`, payload);
      fetchAllIntelligenceRequests();
      showSnackbar('Request declined.', 'success');
    } catch {
      showSnackbar('Failed to decline request.', 'error');
    }
  };

  const hasFetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.idToken) return;
    if (hasFetchedRef.current === user.idToken) return;
    hasFetchedRef.current = user.idToken;
    fetchAllIntelligenceRequests();
  }, [user?.idToken]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setMenuAnchor({ element: event.currentTarget, row });
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleToggleIssuerInfo = (rowId: number) => {
    const isIssuerExpanded = expandedRowIds.includes(rowId);

    // Always close author info for this row
    setExpandedAuthorRowIds((prev) => prev.filter((id) => id !== rowId));

    // Toggle issuer state
    if (isIssuerExpanded) {
      // Closing issuer panel
      setExpandedRowIds((prev) => prev.filter((id) => id !== rowId));
    } else {
      // Opening issuer panel
      setExpandedRowIds((prev) => [...prev, rowId]);
    }

    handleCloseMenu();
  };

  const handleToggleAuthorInfo = (rowId: number) => {
    const isAuthorExpanded = expandedAuthorRowIds.includes(rowId);

    // Always close issuer info for this row
    setExpandedRowIds((prev) => prev.filter((id) => id !== rowId));

    // Toggle author state
    if (isAuthorExpanded) {
      // Closing author panel
      setExpandedAuthorRowIds((prev) => prev.filter((id) => id !== rowId));
    } else {
      // Opening author panel
      setExpandedAuthorRowIds((prev) => [...prev, rowId]);
    }

    handleCloseMenu();
  };

  const handleEditIntelligenceRequest = (request: any) => {
    handleCloseMenu();
    openMessage({
      modalType: 'action',
      header: 'Edit Intelligence Request',
      customBody: (
        <CreateIntelligenceRequestModal
          isEditMode={true}
          intelligenceRequestId={String(request.id)}
          companyId={request.companyId}
          defaultDescription={request.description || ''}
          defaultAmountUsd={request.amountUsd || 150}
          onSuccess={() => {
            fetchAllIntelligenceRequests();
          }}
        />
      ),
      size: 'large',
    });
  };

  const IssuerInfoDetail = ({ row }: { row: any }) => (
    <Box sx={{ p: 3, backgroundColor: 'rgba(59, 111, 217, 0.02)' }}>
      <Paper elevation={0} sx={{ p: 3, border: '1px solid rgba(59, 111, 217, 0.12)' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>
          Intelligence Request Issuer
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>
              ID
            </Typography>
            <Typography variant="body2">{row.issuerData?.id || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>
              Name
            </Typography>
            <Typography variant="body2">
              {`${row.issuerData?.firstName || ''} ${row.issuerData?.lastName || ''}`.trim() || '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>
              Email
            </Typography>
            <Typography variant="body2">{row.issuerData?.email || '—'}</Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );

  const AuthorInfoDetail = ({ row }: { row: any }) => (
    <Box sx={{ p: 3, backgroundColor: 'rgba(108, 168, 255, 0.02)' }}>
      <Paper elevation={0} sx={{ p: 3, border: '1px solid rgba(108, 168, 255, 0.12)' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>
          Target User
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>
              ID
            </Typography>
            <Typography variant="body2">{row.authorData?.id || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>
              Username
            </Typography>
            <Typography variant="body2">{row.authorData?.username || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>
              Name
            </Typography>
            <Typography variant="body2">
              {`${row.authorData?.firstName || ''} ${row.authorData?.lastName || ''}`.trim() || '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>
              Email
            </Typography>
            <Typography variant="body2">{row.authorData?.email || '—'}</Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );

  const columns: GridColDef[] = [
    {
      field: 'companyName',
      headerName: 'Company',
      width: 250,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value && !params.row.companyId) {
          return (
            <CloseIcon
              sx={{
                color: 'text.disabled',
                fontSize: 22,
              }}
            />
          );
        }
        return (
          <Box display="flex" alignItems="center" gap={2}>
            {params.row.companyId && (
              <CompanyLogo
                companyId={params.row.companyId}
                companyName={params.value || '—'}
                sx={{ width: 40, height: 40 }}
              />
            )}
            <Typography variant="body2">{params.value || '—'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      renderCell: (params: GridRenderCellParams) => renderStatusChip(params.value),
    },
    {
      field: 'reviewId',
      headerName: 'Review',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value || params.value === 0) {
          return (
            <CloseIcon
              sx={{
                color: 'text.disabled',
                fontSize: 22,
              }}
            />
          );
        }
        return (
          <CheckCircleIcon
            onClick={() => {
              window.open(
                `/review-reader/${encodeURIComponent(params.row.companyName || '')}/${params.value}`,
                '_blank',
              );
            }}
            sx={{
              color: 'success.main',
              fontSize: 22,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.1)',
                opacity: 0.8,
              },
            }}
          />
        );
      },
    },
    {
      field: 'authorId',
      headerName: 'Target User',
      width: 150,
      valueGetter: (_value, row) => row.authorData?.username || null,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return (
            <CloseIcon
              sx={{
                color: 'text.disabled',
                fontSize: 22,
              }}
            />
          );
        }
        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'engagementType',
      headerName: 'Type',
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        const type = params.value;
        const isPhoneConsultation = type === 'direct' || type === 'consultation';
        return (
          <Chip
            label={isPhoneConsultation ? 'Consult' : 'Review'}
            size="small"
            sx={{
              backgroundColor: isPhoneConsultation ? 'rgba(59, 111, 217, 0.1)' : 'rgba(76, 175, 80, 0.1)',
              color: isPhoneConsultation ? 'primary.main' : 'success.main',
              fontWeight: 600,
              border: `1px solid ${isPhoneConsultation ? 'rgba(59, 111, 217, 0.2)' : 'rgba(76, 175, 80, 0.2)'}`,
            }}
          />
        );
      },
    },
    {
      field: 'amountUsd',
      headerName: 'Payment',
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return (
            <CloseIcon
              sx={{
                color: 'text.disabled',
                fontSize: 22,
              }}
            />
          );
        }
        return (
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
            ${params.value}
          </Typography>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      valueGetter: (_value, row) => row.createdAt,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">{params.value ? moment(params.value).format('MMM D, YYYY') : '—'}</Typography>
      ),
    },
    {
      field: 'isPrivate',
      headerName: 'Visibility',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          className={`intelligence-request-card__chip ${params.value ? 'intelligence-request-card__chip--engagement' : 'intelligence-request-card__chip--bounty'}`}
          icon={params.value ? <LockIcon /> : <LockOpenIcon />}
          label={params.value ? 'Private' : 'Public'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton size="small" onClick={(e) => handleOpenMenu(e, params.row)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  const getDetailPanelContent = (params: any) => {
    const isIssuerExpanded = expandedRowIds.includes(params.row.id);
    const isAuthorExpanded = expandedAuthorRowIds.includes(params.row.id);

    if (isIssuerExpanded) {
      return <IssuerInfoDetail row={params.row} />;
    }
    if (isAuthorExpanded) {
      return <AuthorInfoDetail row={params.row} />;
    }
    return null;
  };

  const getDetailPanelHeight = (): 'auto' => {
    return 'auto';
  };

  // Combine both expanded row arrays for DataGrid controlled mode (must be a Set)
  const allExpandedRowIds = new Set([...expandedRowIds, ...expandedAuthorRowIds]);

  return (
    <Box>
      <ModernDataTable
        title="All Intelligence Requests"
        subtitle="All intelligence requests across the platform, including pending, fulfilled, and private requests."
        rows={items}
        columns={columns}
        loading={loading}
        initialPageSize={10}
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        apiRef={apiRef}
        detailPanelExpandedRowIds={allExpandedRowIds}
      />

      <Menu
        anchorEl={menuAnchor?.element}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => handleEditIntelligenceRequest(menuAnchor?.row)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        <MenuItem onClick={() => handleToggleIssuerInfo(menuAnchor?.row?.id)}>
          <ListItemIcon>
            <InfoOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={expandedRowIds.includes(menuAnchor?.row?.id) ? 'Hide Issuer Info' : 'View Issuer Info'}
          />
        </MenuItem>
        {menuAnchor?.row?.authorData && (
          <MenuItem onClick={() => handleToggleAuthorInfo(menuAnchor?.row?.id)}>
            <ListItemIcon>
              <InfoOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={expandedAuthorRowIds.includes(menuAnchor?.row?.id) ? 'Hide Author Info' : 'View Author Info'}
            />
          </MenuItem>
        )}
        {menuAnchor?.row?.reviewId && menuAnchor.row.reviewId > 0 && (
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              window.open(
                `/review-reader/${encodeURIComponent(menuAnchor.row.companyName || '')}/${menuAnchor.row.reviewId}`,
                '_blank',
              );
            }}
          >
            <ListItemIcon>
              <RemoveRedEyeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Read Deal Retro" />
          </MenuItem>
        )}
        {menuAnchor?.row?.status === IntelligenceRequestStatus.PENDING && (
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              handleMarkAsActive(menuAnchor.row);
            }}
          >
            <ListItemIcon>
              <DynamicIcon iconName={IconName.Check} />
            </ListItemIcon>
            <ListItemText primary="Mark as Active" />
          </MenuItem>
        )}
        {(menuAnchor?.row?.status === IntelligenceRequestStatus.ACTIVE ||
          menuAnchor?.row?.status === IntelligenceRequestStatus.ACCEPTED_BY_REVIEW_AUTHOR) && (
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              handleFulfillIntelligenceRequest(menuAnchor.row);
            }}
          >
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText primary="Mark as Fulfilled" />
          </MenuItem>
        )}
        {menuAnchor?.row && (
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              handleDeclineIntelligenceRequest(menuAnchor.row);
            }}
          >
            <ListItemIcon>
              <DynamicIcon iconName={IconName.DeleteOutline} />
            </ListItemIcon>
            <ListItemText primary="Delete" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default AdminIntelligenceRequests;
